import React from 'react';
import { GraduationCap, Users, Award, GitBranch, Code, Building2, ShieldCheck, Lock, Target, Briefcase, Bot, Sparkles, Handshake } from 'lucide-react';
interface Service {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
  bgColor: string;
}
const services: Service[] = [{
  icon: GraduationCap,
  title: 'Career Development Coaching',
  description: 'Personalized guidance to accelerate your career trajectory in technology and leadership.',
  color: 'text-blue-600',
  bgColor: 'bg-blue-50'
}, {
  icon: Users,
  title: 'Corporate Training',
  description: 'Tailored training programs for teams on modern technology practices, security, and architecture.',
  color: 'text-emerald-600',
  bgColor: 'bg-emerald-50'
}, {
  icon: Award,
  title: 'Leadership Development',
  description: 'Servant leadership frameworks and executive coaching for high-tech environments.',
  color: 'text-purple-600',
  bgColor: 'bg-purple-50'
}, {
  icon: GitBranch,
  title: 'End-to-End SDLC Process & Visibility',
  description: 'Complete software development lifecycle optimization with full transparency and governance.',
  color: 'text-cyan-600',
  bgColor: 'bg-cyan-50'
}, {
  icon: Code,
  title: 'Custom Software Development',
  description: 'Bespoke software solutions built with enterprise-grade architecture and security standards.',
  color: 'text-orange-600',
  bgColor: 'bg-orange-50'
}, {
  icon: ShieldCheck,
  title: 'Information Security',
  description: 'Comprehensive security assessments, frameworks, and regulatory-grade compliance strategies.',
  color: 'text-rose-600',
  bgColor: 'bg-rose-50'
}, {
  icon: Building2,
  title: 'Enterprise & Business Architecture',
  description: 'Strategic architecture design for scalable, resilient, and future-proof systems.',
  color: 'text-indigo-600',
  bgColor: 'bg-indigo-50'
}, {
  icon: Lock,
  title: 'Cybersecurity',
  description: 'Advanced threat protection, post-quantum readiness, and security posture assessments.',
  color: 'text-red-600',
  bgColor: 'bg-red-50'
}, {
  icon: Target,
  title: 'Strategic Planning',
  description: 'Technology roadmaps and strategic initiatives aligned with business objectives.',
  color: 'text-amber-600',
  bgColor: 'bg-amber-50'
}, {
  icon: Briefcase,
  title: 'Business Consulting',
  description: 'Expert advisory on digital transformation, market positioning, and operational efficiency.',
  color: 'text-teal-600',
  bgColor: 'bg-teal-50'
}, {
  icon: Bot,
  title: 'AI Product Creation',
  description: 'End-to-end AI product development from ideation to deployment with ethical AI practices.',
  color: 'text-violet-600',
  bgColor: 'bg-violet-50'
}, {
  icon: Sparkles,
  title: 'AI Utilization for Efficiency',
  description: 'Leverage AI to streamline operations, automate workflows, and drive measurable productivity gains.',
  color: 'text-fuchsia-600',
  bgColor: 'bg-fuchsia-50'
}];
export function ConsultingSection() {
  return <section id="consulting" className="py-24 bg-[#0B1120] px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-83" data-mp="3hq4mnqer7fq4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-84" data-mp="e82zma0ptyfd">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl opacity-40" data-id="element-85" data-mp="eaz1g9fop793"></div>
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl opacity-40" data-id="element-86" data-mp="4j2zuoee2z04"></div>
      </div>

      <div className="max-w-7xl mx-auto relative" data-id="element-87" data-mp="godhubihh43">
        <div className="text-center mb-16" data-id="element-88" data-mp="wzm0obb6j2er">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500/20 rounded-2xl mb-4 border border-cyan-500/30 glow-cyan backdrop-blur-sm" data-id="element-89" data-mp="x0nmnatqh9b9">
            <Handshake className="h-8 w-8 text-cyan-400" data-id="element-90" data-mp="cr5qiv1ycqs2" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-91" data-mp="csyvm5wx32xy">
            Consulting Services
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto rounded-full mb-6" data-id="element-92" data-mp="3qhbbx0tokdl"></div>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-93" data-mp="7r9kkq9p2p9v">
            Leveraging decades of experience across defense, fintech, and
            enterprise systems to help organizations build, scale, and secure
            their technology.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-94" data-mp="mp1zbg7f39n0">
          {services.map((service, index) => <div key={index} className="group bg-white/5 backdrop-blur-sm rounded-xl p-6 shadow-sm hover:shadow-lg border border-white/10 hover:border-cyan-500/30 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1 flex flex-col" data-id="element-95" data-mp="qplx8lzteha7" data-mpi={index}>
              <div className={`inline-flex items-center justify-center w-12 h-12 bg-white/5 border border-white/10 rounded-xl mb-4 group-hover:scale-110 transition-transform`} data-id="element-96" data-mp="33wljuk9u8oqt">
                <service.icon className={`h-6 w-6 ${service.color.replace('600', '400')}`} data-id="element-97" data-mp="3dm515lhwkajk" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors" data-id="element-98" data-mp="38qfz9bieuhk3">
                {service.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed flex-grow" data-id="element-99" data-mp="2g0puvu6kyu16">
                {service.description}
              </p>
            </div>)}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center" data-id="element-100" data-mp="2pwsvuwpp4mmj">
          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-cyan-500/20 shadow-sm glow-cyan" data-id="element-101" data-mp="2pzykybybroi9">
            <h3 className="text-2xl font-bold text-white mb-4" data-id="element-102" data-mp="56v44rsvdvin">
              Ready to Transform Your Organization?
            </h3>
            <p className="text-slate-300 max-w-xl mx-auto mb-8" data-id="element-103" data-mp="ccxix0g8asdc">
              Whether you need strategic guidance, hands-on technical
              leadership, or AI-driven innovation, let's discuss how to move
              your business forward.
            </p>
            <a href="#contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-cyan-500 text-black font-semibold rounded-lg hover:bg-cyan-400 transition-colors shadow-lg" data-id="element-104" data-mp="r7bwglh0x9km">
              Get Started
              <Sparkles className="h-4 w-4" data-id="element-105" data-mp="3ij93degwa1m" />
            </a>
          </div>
        </div>
      </div>
    </section>;
}